import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Text, { ErrorDescription } from 'components/base/Text';
import { InputAdornment } from '@material-ui/core';
import { Flex, Padding } from 'components/base/layout-components';
import isEmail from 'validator/lib/isEmail';
import Icon from 'components/base/Icon';
import { LOCAL_STORAGE_KEYS } from 'common/constants/constants';
import BrowserStorageAccessor from 'common/utils/BrowserStorageAccessor';
import { generateSAMLLoginUrl } from 'common/utils/utils';
import { ErrorBox } from 'components/InfoBox';
import { Button } from '@linearb/linui-common/marketing/Button';
import { TextField } from '@linearb/linui-common/marketing/TextField';

const SSOContainer = styled(Flex)`
  width: 32rem;
  margin-top: 1rem;
`;

export const SSO_ERRORS = {
  wrongEmail: 'Incorrect email address. Please try again',
  wrongSAMLConfiguration:
    'Incorrect SAML configuration. Please contact your admin'
};

const getLoginEmailFromLocalStor = localStorageAccessor => {
  const loginEmailLocStor = localStorageAccessor.get(
    LOCAL_STORAGE_KEYS.SSO_LOGIN_EMAIL
  ); // On login we are storing the email in local storage
  try {
    return loginEmailLocStor.trim();
  } catch (e) {}
  return '';
};

const SSOLogin = ({ isEnabled = false, ssoError = null, error }) => {
  const ssoErrMessage = SSO_ERRORS?.[ssoError] ?? error?.message ?? null;
  const localStorageAccessor = BrowserStorageAccessor().local;
  const ssoShowEmail = JSON.parse(
    localStorageAccessor.get(LOCAL_STORAGE_KEYS.SSO_SHOW_EMAIL)
  );

  if (!ssoErrMessage) {
    localStorageAccessor.remove(LOCAL_STORAGE_KEYS.SSO_LOGIN_EMAIL);
  }

  const [showEmail, setShowEmail] = useState(!!(ssoError || ssoShowEmail));
  const [email, setEmail] = useState(
    getLoginEmailFromLocalStor(localStorageAccessor)
  );

  useEffect(() => {
    localStorageAccessor.set(LOCAL_STORAGE_KEYS.SSO_SHOW_EMAIL, showEmail);
  }, [showEmail]);

  const onGoClicked = async () => {
    localStorageAccessor.set(LOCAL_STORAGE_KEYS.SSO_LOGIN_EMAIL, email);
    // Use a small delay to ensure the value is set before redirecting
    setTimeout(() => {
      window.location.href = generateSAMLLoginUrl(
        email,
        localStorageAccessor.get(LOCAL_STORAGE_KEYS.TEST_CONNECTIVITY)
      );
    }, 100); // Delay of 100 milliseconds
  };

  return (
    <SSOContainer direction='column' items='center'>
      <Button
        size='small'
        variant='outlined'
        startIcon={<Icon name='VPNKey' color='shuttleGray' size='small' />}
        onClick={() => setShowEmail(!showEmail)}
        data-testid='sso'
      >
        SSO
      </Button>
      <Padding t='medium' width='100%'>
        {error && !showEmail && (
          <Flex justify='center' mt='0.8rem'>
            <ErrorBox>
              <Text>Identification Failed.</Text>
              <Text>{error.message}</Text>
            </ErrorBox>
          </Flex>
        )}
        {showEmail && (
          <>
            <Text color='shuttleGray' size='small' pb='0.2rem'>
              EMAIL
            </Text>
            <Flex gap={'1rem'} items='center'>
              <TextField
                value={email}
                disabled={!isEnabled}
                inputProps={{
                  'data-testid': `email-text-field-input`,
                  style: { fontSize: '1.3rem' }
                }}
                placeholder='email@company.com'
                data-testid='email-text-field'
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <Icon name='VPNKey' color='shuttleGray' size='medium' />
                    </InputAdornment>
                  ),
                  disableUnderline: true
                }}
                onChange={event => setEmail(event.target.value)}
              />

              <Button
                size='sm'
                data-testid='go-button'
                disabled={!isEmail(email) || !isEnabled}
                onClick={onGoClicked}
              >
                GO
              </Button>
            </Flex>
            {ssoErrMessage && (
              <ErrorDescription>{ssoErrMessage}</ErrorDescription>
            )}
          </>
        )}
      </Padding>
    </SSOContainer>
  );
};

export default SSOLogin;
